<script>
import FullCalendar from '@fullcalendar/vue3'
import resourceTimelinePlugin from '@fullcalendar/vue3-scheduler/resource-timeline';
import interactionPlugin from '@fullcalendar/vue3/interaction'
import { put, get } from '@rails/request.js'
import ModalDialogMixin from '../mixins/modal-dialog.vue'
import TimetableMixin from '../mixins/timetable.vue'

import luxonFormatPlugin from '@fullcalendar/format-luxon3'
import themePlugin from '@fullcalendar/vue3/themes/forma'
import '@fullcalendar/vue3/skeleton.css'
import '@fullcalendar/vue3/themes/forma/theme.css'
import '@fullcalendar/vue3/themes/forma/palettes/blue.css'
import clsx from 'clsx'

export default {
  components: {
    FullCalendar
  },
  mixins: [TimetableMixin, ModalDialogMixin],
  props: {
    eventId: String,
    positionsUrl: String,
    requirementsUrl: String,
    shiftsUrl: String,
    shiftVolunteerUrl: String,
    eventStartDate: String,
    eventEndDate: String,
  },
  data() {
    return {
      calendarOptions: {
        plugins: [luxonFormatPlugin, interactionPlugin, resourceTimelinePlugin, themePlugin],
        schedulerLicenseKey: 'AGPL-My-Frontend-And-Backend-Are-Open-Source',
        initialView: 'resourceTimeline',
        height: "auto",
        nowIndicator: true,
        locale: document.documentElement.lang,
        timeZone: this.timeZone(),
        slotDuration: '00:15:00',
        slotHeaderInterval: '00:30',
        eventOrder: "orderFirst,start,-duration,allDay,title",
        titleFormat: 'ccc, DDD',
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
        },
        slotHeaderFormat: {
          hour: 'numeric',
          minute: '2-digit',
          omitZeroMinute: false,
        },
        headerToolbar: {
          left:   'title',
          right:  'toggleSlotHeaderInterval prev,next'
        },
        buttons: {
          toggleSlotHeaderInterval: {
            text: this.$i18n.t("timetable.buttons.toggle_slot_header_interval"),
            click: this.toggleSlotHeaderInterval,
            iconClass: 'bi bi-arrows-collapse-vertical',
            display: 'icon',
          }
        },
        validRange: {
          start: this.eventStartDate,
          end: this.eventEndDate
        },
        events: [{
          start: this.eventStartDate,
          end: this.eventEndDate,
          editable: false,
          display: 'inverse-background',
          color: 'grey'
        }],
        eventSources: [
          {
            id: "shifts",
            url: this.shiftsUrl,
          },
          {
            id: "requirements",
            url: this.requirementsUrl,
          },
        ],
        eventDrop: this.onDropOrResize,
        eventResize: this.onDropOrResize,
        dragRevertDuration: 0,
        selectable: true,
        select: this.onSelection,
        eventLongPressDelay: 300,
        eventClick: this.onClick,
        datesSet: this.onDateChanged,
        resources: this.positionsUrl,
        resourceGroupField: 'divisionName',
        resourceOrder: ['divisionName', 'title'],
        resourceColumns: [
          {
            field: 'title',
            headerContent: this.$i18n.t("timetable.positions.headers.position"),
          },
        ]
      },
      requirementView: false,
    }
  },
  channels: {
    EventsChannel: {
      received(data) {
        switch (data.event) {
          case "shifts_changed":
            for (const volunteerId of data.volunteer_ids) {
              this.refreshShiftsOfVolunteer(volunteerId)
            }
            break;
          case "shift_deleted":
            this.getEventByIdAndSource(data.shift_id, 'shifts')?.remove()
            break;
          case "requirements_changed":
            for (const positionId of data.position_ids) {
              this.refreshRequirementOfPosition(positionId)
            }
            break;
          case "requirement_deleted":
            this.getEventByIdAndSource(data.requirement_id, 'requirements')?.remove()
            break;
          case "positions_changed":
            this.$refs.calendar.getApi().refetchResources()
            break;
        }
      }
    },
  },
  mounted() {
    this.$cable.subscribe({
      channel: "EventsChannel",
      eventId: this.eventId,
    });
  },
  methods: {
    activateEditRequirementsView: async function() {
      this.requirementView = true

      document.querySelector("#dashboard-positions-btn-requirements").classList.add("btn-secondary")
      document.querySelector("#dashboard-positions-btn-requirements").classList.remove("btn-outline-secondary")

      document.querySelector("#dashboard-positions-btn-shifts").classList.remove("btn-secondary")
      document.querySelector("#dashboard-positions-btn-shifts").classList.add("btn-outline-secondary")

      // prevents glitching as the height of shifts change while extending/collapsing
      this.$refs.calendar.getApi().render()
    },
    activateEditShiftsView: async function() {
      this.requirementView = false

      document.querySelector("#dashboard-positions-btn-requirements").classList.remove("btn-secondary")
      document.querySelector("#dashboard-positions-btn-requirements").classList.add("btn-outline-secondary")

      document.querySelector("#dashboard-positions-btn-shifts").classList.add("btn-secondary")
      document.querySelector("#dashboard-positions-btn-shifts").classList.remove("btn-outline-secondary")

      // prevents glitching as the height of shifts change while extending/collapsing
      this.$refs.calendar.getApi().render()
    },
    onDropOrResize: async function(eventResizeOrDropInfo) {
      var shift_or_requirement = eventResizeOrDropInfo.event;

      if (typeof shift_or_requirement.id == 'undefined') {
        eventResizeOrDropInfo.revert();
        return;
      }

      if (shift_or_requirement.source.id === 'shifts') {
        return await this.onDropOrResizeShift(eventResizeOrDropInfo)
      }

      return await this.onDropOrResizeRequirement(eventResizeOrDropInfo)

    },
    onDropOrResizeShift: async function(eventResizeOrDropInfo) {
      const shift = eventResizeOrDropInfo.event

      const response = await put(shift.extendedProps.shiftUrl, { body: JSON.stringify({
        shift: {
          starts_at: shift.start.toISOString(),
          ends_at: shift.end.toISOString(),
          position_id: shift.getResources()[0].id
        }
      })})
      if (!response.ok) {
        eventResizeOrDropInfo.revert();
      }
    },
    onDropOrResizeRequirement: async function(eventResizeOrDropInfo) {
      const requirement = eventResizeOrDropInfo.event

      const response = await put(requirement.extendedProps.requirementUrl, { body: JSON.stringify({
        requirement: {
          starts_at: requirement.start.toISOString(),
          ends_at: requirement.end.toISOString(),
        }
      })})
      if (!response.ok) {
        eventResizeOrDropInfo.revert();
      }
    },
    onSelection: async function(selectionInfo) {
      if (selectionInfo.end - selectionInfo.start < 1800000) {
        // Check against a minimal duration of 30 minutes to skip random clicks directly on the calendar (e.g. to close modal dialogs)
        return
      }

      if (this.requirementView) {
        return await this.onSelectionRequirement(selectionInfo)
      }

      return await this.onSelectionShift(selectionInfo)
    },
    onSelectionShift: async function(selectionInfo) {
      const response = await get(this.shiftVolunteerUrl, { query: {
        "shift[starts_at]": selectionInfo.start.toISOString(),
        "shift[ends_at]": selectionInfo.end.toISOString(),
        "shift[position_id]": selectionInfo.resource.id,
      }})
      if (!response.ok) {
        return
      }

      const content = await response.text
      this.showModalDialogWithContent(content, 'xl')
    },
    onSelectionRequirement: async function(selectionInfo) {
      const response = await get(selectionInfo.resource.extendedProps.requirementCreateUrl, { query: {
        "requirement[starts_at]": selectionInfo.start.toISOString(),
        "requirement[ends_at]": selectionInfo.end.toISOString(),
      }})
      if (!response.ok) {
        return
      }

      const content = await response.text
      this.showModalDialogWithContent(content, 'sm')
    },
    refreshRequirementOfPosition: async function(positionId) {
      const calendarApi = this.$refs.calendar.getApi()
      const position = calendarApi.getResourceById(positionId)

      if (!position) {
        return
      }

      const response = await get(this.requirementsUrl, { query: {
        "start": calendarApi.view.currentStart.toISOString(),
        "end": calendarApi.view.currentEnd.toISOString(),
        "position_id": positionId,
      }})
      if (!response.ok) {
        return
      }

      const requirementEventSource = calendarApi.getEventSourceById("requirements")
      const content = await response.json
      for (const requirement of content) {
        this.getEventByIdAndSource(requirement.id, 'requirements')?.remove()
        calendarApi.addEvent(requirement, requirementEventSource)
      }
    },
  }
}
</script>
<template>
  <div style="height: 0px; position: relative; top: -40px; right: 3rem;" class="text-end">
    <div class="btn-group">
      <button
        id="dashboard-positions-btn-shifts"
        class="btn btn-secondary"
        @click="this.activateEditShiftsView"
        :title="this.$i18n.t('timetable.positions.buttons.edit_shifts.label')"
        >
        <span class="bi bi-calendar2-range me-1"></span>
        <span class="d-none d-md-inline">{{ this.$i18n.t("timetable.positions.buttons.edit_shifts.label") }}</span>
      </button>
      <button 
        id="dashboard-positions-btn-requirements"
        class="btn btn-outline-secondary"
        @click="this.activateEditRequirementsView"
        :title="this.$i18n.t('timetable.positions.buttons.edit_requirements.label')"
        >
        <span class="bi bi-exclamation-diamond me-1"></span>
        <span class="d-none d-md-inline">{{ this.$i18n.t("timetable.positions.buttons.edit_requirements.label") }}</span>
      </button>
    </div>
  </div>

  <FullCalendar :options="calendarOptions" ref="calendar">
    <template v-slot:resourceGroupHeaderContent='arg'>
      {{ arg.fieldValue }}
    </template>

    <template v-slot:resourceCellContent='arg'>
      <span class="bi-icon bi-circle-fill me-1" :style="{color: arg.resource.extendedProps.color}"></span>
      <div style="display: inline-table">
        <span>
          <a :href="arg.resource.extendedProps.url">{{ arg.resource.title }}</a>
          <span v-if="arg.resource.extendedProps.comment" class="small"><br />{{ arg.resource.extendedProps.comment }}</span>
        </span>
      </div>
    </template>

    <template v-slot:eventContent='arg'>
      <VueTimetableShift v-if="arg.event.source.id == 'shifts'" :event="arg.event" />
      <VueTimetableRequirement v-else :event="arg.event" />
    </template>
  </FullCalendar>
</template>
